/**
 * AddCostplusProjectModal — paste a website's Cost+ API key + a friendly
 * name. The orchestrator validates the key against the live Cost+ gateway
 * before persisting; on 401 we surface the error inline. Used by both
 * the Sources page and the checkout-page node drawer.
 *
 * Naming note: the orchestrator's data model still calls these rows
 * "Cost+ projects" (the underlying gateway terminology), but the
 * merchant-facing label is "website" — that's how a checkout merchant
 * thinks about each storefront they bind to a Cost+ account.
 */

import { type FormEvent, useState } from "react";
import { Info, X } from "lucide-react";
import { getApiClient } from "../../lib/apiClient";
import type { CostplusProject } from "../../types/api";
import { ApiError } from "../../types/api";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (project: CostplusProject) => void;
}

// Gateway host choices shown in the picker. Keep the default first —
// Cost+ gateway has superseded the legacy Nopayn backend. Merchants
// pick "Legacy" only when their project lives on the older cluster.
const API_BASE_CHOICES = [
  { value: "", label: "Cost+ gateway (api.costplus.online) — default" },
  { value: "https://api.nopayn.co.uk/v1", label: "Legacy (api.nopayn.co.uk)" },
] as const;

export function AddCostplusProjectModal({ open, onClose, onCreated }: Props) {
  const [name, setName] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [apiBaseURL, setApiBaseURL] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  function reset() {
    setName("");
    setApiKey("");
    setIsDefault(false);
    setApiBaseURL("");
    setError(null);
    setSubmitting(false);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !apiKey.trim()) {
      setError("Website name and API key are required.");
      return;
    }
    setSubmitting(true);
    try {
      const project = await getApiClient().costplusProjects.add({
        // The merchant only fills in name + api_key. Internal IDs are
        // generated server-side when these come through empty.
        project_name: name.trim(),
        costplus_merchant_id: "",
        project_id: "",
        api_key: apiKey.trim(),
        is_default: isDefault,
        api_base_url: apiBaseURL || undefined,
      });
      onCreated(project);
      reset();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === "COSTPLUS_KEY_INVALID") {
        setError("Cost+ rejected this API key (401). Double-check the key and try again.");
      } else if (err instanceof ApiError && err.code === "COSTPLUS_PROJECT_EXISTS") {
        setError("That website is already linked.");
      } else {
        setError(err instanceof Error ? err.message : "Failed to add website.");
      }
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white dark:bg-neutral-950 shadow-xl border border-neutral-200 dark:border-neutral-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold">Add a website</h3>
            <p className="text-[11px] text-neutral-500 mt-0.5">
              Paste your Cost+ project's API key. We'll verify it before
              saving so you don't ship a typo.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={onSubmit} className="p-4 space-y-3">
          <Field
            label="Website name"
            hint="Whatever you call this storefront. Shown when you bind a checkout to it."
          >
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Main store"
              className="w-full rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-2.5 py-1.5 text-sm"
              autoFocus
            />
          </Field>
          <Field
            label={
              <span className="inline-flex items-center gap-1">
                Website API key
                <span
                  className="text-neutral-400 cursor-help"
                  title="Found in your Cost+ Dashboard under the website's settings → API keys. Each website (project) has its own key. Copy and paste it here. It's stored encrypted and never shown again."
                >
                  <Info className="w-3 h-3" />
                </span>
              </span>
            }
            hint="Stored encrypted. Never displayed in plain text after save."
          >
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="sk_live_…"
              className="w-full rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-2.5 py-1.5 text-sm font-mono"
              autoComplete="off"
            />
          </Field>
          <Field
            label="Gateway backend"
            hint="Cost+ is the current gateway. Switch to Legacy only if this project still lives on the older Nopayn backend."
          >
            <select
              value={apiBaseURL}
              onChange={(e) => setApiBaseURL(e.target.value)}
              className="w-full rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-2.5 py-1.5 text-sm"
            >
              {API_BASE_CHOICES.map((choice) => (
                <option key={choice.value || "default"} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
          </Field>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              className="rounded border-neutral-300 dark:border-neutral-600"
            />
            Mark as default website
          </label>
          {error ? (
            <p className="text-xs text-category-error border border-category-error/30 bg-category-error/5 rounded-md px-2.5 py-2">
              {error}
            </p>
          ) : null}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-neutral-200 dark:border-neutral-700 px-3 py-1.5 text-xs font-medium hover:bg-neutral-50 dark:hover:bg-neutral-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md bg-category-trigger/10 border border-category-trigger/30 text-category-trigger px-3 py-1.5 text-xs font-medium hover:bg-category-trigger/20 disabled:opacity-50"
            >
              {submitting ? "Verifying…" : "Add website"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: React.ReactNode;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300">{label}</span>
      {hint ? <span className="block text-[11px] text-neutral-500 mt-0.5">{hint}</span> : null}
      <div className="mt-1">{children}</div>
    </label>
  );
}
